import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, className, id, type, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");
    const errorId = error ? `${inputId}-error` : undefined;

    // Password visibility toggle — owner request 2026-04-15. When the
    // caller passes type="password", we render an eye button that
    // flips the input to type="text" so the user can verify what they
    // typed. Non-password inputs are unchanged.
    const isPassword = type === "password";
    const [revealed, setRevealed] = useState(false);
    const effectiveType = isPassword ? (revealed ? "text" : "password") : type;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-brand-muted"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            className={cn(
              "w-full border-0 border-b border-brand-dark/20 bg-transparent px-0 py-2.5 text-base sm:text-sm text-brand-dark placeholder:text-brand-muted/50",
              "transition-all duration-300",
              "focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/30 focus:ring-offset-1",
              "focus:shadow-[0_0_0_3px_rgba(165,141,118,0.08)]",
              "disabled:cursor-not-allowed disabled:opacity-40",
              // Leave room on the right for the eye toggle when needed.
              isPassword && "pe-9",
              error && "border-red-500 focus:border-red-500 focus:ring-red-500/20 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.08)]",
              className
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              className="absolute end-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-8 h-8 text-brand-muted hover:text-brand-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 rounded"
              aria-label={revealed ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {revealed ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-500 animate-error-appear">{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
